import { useState, useRef, useCallback } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import { setUserRole } from '../../hooks/useFirestore';
import { useToast } from '../../contexts/ToastContext';
import { cn } from '../../utils/cn';

/* ── CSV column → user field mapping ── */
const FIELD_MAP = {
  email:        'email',
  useremail:    'email',
  user_email:   'email',
  role:         'role',
  userrole:     'role',
  user_role:    'role',
  isadmin:      'isAdmin',
  is_admin:     'isAdmin',
  admin:        'isAdmin',
  iscommunicator: 'isCommunicator',
  is_communicator: 'isCommunicator',
  communicator: 'isCommunicator',
};

const TRUTHY = new Set(['true', '1', 'yes', 'y', 'x']);

const CSV_TEMPLATE_HEADERS = ['email', 'role'];

/* ── Helpers ── */
function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { result.push(current.trim()); current = ''; }
      else { current += ch; }
    }
  }
  result.push(current.trim());
  return result;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = parseCsvLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const vals = parseCsvLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

function mapRow(raw) {
  const mapped = {};
  for (const [csvCol, value] of Object.entries(raw)) {
    const key = FIELD_MAP[csvCol.toLowerCase().replace(/[\s-]/g, '')];
    if (key) mapped[key] = value;
  }

  // Resolve role: support both "role" column and separate isAdmin/isCommunicator columns
  let isAdmin = false;
  let isCommunicator = false;

  if (mapped.role) {
    const role = mapped.role.toLowerCase().trim();
    if (role === 'admin') { isAdmin = true; }
    else if (role === 'communicator') { isCommunicator = true; }
    else if (role === 'both' || role === 'admin,communicator' || role === 'communicator,admin') {
      isAdmin = true;
      isCommunicator = true;
    }
  }
  if (mapped.isAdmin) isAdmin = isAdmin || TRUTHY.has(mapped.isAdmin.toLowerCase().trim());
  if (mapped.isCommunicator) isCommunicator = isCommunicator || TRUTHY.has(mapped.isCommunicator.toLowerCase().trim());

  return {
    email: mapped.email?.trim().toLowerCase() || '',
    isAdmin,
    isCommunicator,
  };
}

function downloadTemplate() {
  const csv = CSV_TEMPLATE_HEADERS.join(',') + '\n'
    + 'alice@forter.com,communicator\n'
    + 'bob@forter.com,communicator\n'
    + 'charlie@forter.com,admin\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'user_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Component ── */
export default function BulkUserImportModal({ open, onClose }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const processFile = useCallback((f) => {
    if (!f || !f.name.endsWith('.csv')) {
      toast.error('Please upload a .csv file');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCsv(e.target.result);
      if (rows.length === 0) {
        toast.error('CSV is empty or has no data rows');
        return;
      }
      const mapped = rows.map(mapRow);
      const valid = mapped.filter((r) => r.email && r.email.includes('@') && (r.isAdmin || r.isCommunicator));
      const invalid = mapped.length - valid.length;

      setPreview({
        headers,
        rows: valid,
        totalParsed: mapped.length,
        invalidCount: invalid,
        adminCount: valid.filter((r) => r.isAdmin).length,
        communicatorCount: valid.filter((r) => r.isCommunicator).length,
      });
    };
    reader.readAsText(f);
  }, [toast]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    processFile(f);
  }, [processFile]);

  const handleImport = async () => {
    if (!preview?.rows?.length) return;
    setImporting(true);
    let added = 0;
    const errors = [];
    try {
      for (const row of preview.rows) {
        try {
          await setUserRole(row.email, { isAdmin: row.isAdmin, isCommunicator: row.isCommunicator });
          added++;
        } catch (e) {
          errors.push({ email: row.email, error: e.message });
        }
      }
      setResult({ added, errors });
      if (errors.length === 0) {
        toast.success(`Added/updated ${added} users`);
      } else {
        toast.error(`Import completed with ${errors.length} error(s)`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Import failed: ' + e.message);
    }
    setImporting(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Bulk Import Users"
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <button onClick={downloadTemplate} className="text-sm text-accent-600 hover:text-accent-700 font-medium flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
            Download Template
          </button>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            {preview && !result && (
              <Button onClick={handleImport} loading={importing}>
                Import {preview.rows.length} User{preview.rows.length !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Upload zone */}
        {!preview && !result && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className={cn(
              'border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all',
              dragOver ? 'border-accent-400 bg-accent-50/50' : 'border-surface-300 hover:border-accent-300 hover:bg-surface-50',
            )}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => processFile(e.target.files?.[0])}
            />
            <div className="w-14 h-14 mx-auto rounded-2xl bg-surface-100 flex items-center justify-center text-3xl mb-4">📊</div>
            <p className="text-sm font-semibold text-navy-800 mb-1">
              {file ? file.name : 'Drop your CSV file here or click to browse'}
            </p>
            <p className="text-xs text-navy-400">
              Columns: <span className="font-mono text-navy-600">email, role</span> (admin / communicator / both)
            </p>
          </div>
        )}

        {/* Preview */}
        {preview && !result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Parsed', value: preview.totalParsed, icon: '📄' },
                { label: 'Valid', value: preview.rows.length, icon: '✅' },
                { label: 'Admins', value: preview.adminCount, icon: '🔑' },
                { label: 'Communicators', value: preview.communicatorCount, icon: '📨' },
              ].map((s) => (
                <div key={s.label} className="bg-surface-50 rounded-xl p-3 text-center">
                  <div className="text-lg mb-0.5">{s.icon}</div>
                  <p className="text-xs text-navy-400 font-semibold uppercase tracking-wider">{s.label}</p>
                  <p className="text-lg font-bold text-navy-900">{s.value}</p>
                </div>
              ))}
            </div>
            {preview.invalidCount > 0 && (
              <p className="text-xs text-amber-600 font-medium">
                ⚠ {preview.invalidCount} row(s) skipped (missing email or role)
              </p>
            )}
            <div className="rounded-xl border border-surface-200 overflow-hidden max-h-60 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-surface-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold text-navy-500">Email</th>
                    <th className="text-left px-4 py-2 font-semibold text-navy-500">Roles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {preview.rows.slice(0, 50).map((r, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 text-navy-800 font-medium">{r.email}</td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1.5">
                          {r.isAdmin && <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-red-50 text-red-700 ring-1 ring-inset ring-red-200">Admin</span>}
                          {r.isCommunicator && <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">Communicator</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.rows.length > 50 && (
                <p className="text-xs text-navy-400 text-center py-2">…and {preview.rows.length - 50} more</p>
              )}
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
              <span className="text-2xl">✅</span>
              <div>
                <p className="text-sm font-semibold text-emerald-900">Import Complete</p>
                <p className="text-xs text-emerald-700">{result.added} user(s) added/updated successfully.</p>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="rounded-xl border border-red-200 p-4 bg-red-50 space-y-1">
                <p className="text-sm font-semibold text-red-900">{result.errors.length} Error(s)</p>
                {result.errors.slice(0, 10).map((e, i) => (
                  <p key={i} className="text-xs text-red-700">{e.email}: {e.error}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
